/**
 * View
 *
 * @description Using Backbone without jQuery
 * @author Daniel Kvistgaard <dkvistgaard@vertic.com>
 */

import Backbone from 'backbone';

class ViewEventClass {
	constructor() {
		this.functionMap = {};
	}

	addEventListener(element, event, selector, func) {
		const eventName = event.split('.')[0];
		const namespace = event.split('.').slice(1).join('.');

		this.functionMap[event] = {
			eventName,
			namespace,
			event,
			selector,
			func,
		};

		element.addEventListener(eventName, this.functionMap[event].func);
	}

	removeEventListener(element, event, selector, func) {
		const eventName = event.split('.')[0];
		const namespace = event.split('.').slice(1).join('.');

		Object.keys(this.functionMap).forEach((key) => {
			const handler = this.functionMap[key];

			if (!eventName && handler.namespace === namespace) {
				ViewEventClass.removeEventListener(element, handler.eventName, handler.func);
				delete this.functionMap[key];
			} else if (eventName && handler.event === event && handler.selector === selector && handler.func === func) {
				ViewEventClass.removeEventListener(element, eventName, handler.func);
				delete this.functionMap[key];
			}
		});
	}

	static removeElementEventListener(element, event, func) {
		const eventName = event.split('.')[0];

		if (eventName) {
			element.removeEventListener(eventName, func);
		}
	}
}

const ViewEvent = new ViewEventClass();

const View = Backbone.View.extend({
	$(selector) {
		return this.el.querySelectorAll(selector);
	},

	// Remove this view's element from the document and all event listeners
	// attached to it. Exposed for subclasses using an alternative DOM
	// manipulation API.
	_removeElement() {
		this.el.parentNode.removeChild(this.el);
	},

	// Creates the `this.el` and `this.$el` references for this view using the
	// given `el`. `el` can be a CSS selector or an HTML string, a jQuery
	// context or an element. Subclasses can override this to utilize an
	// alternative DOM manipulation API and are only required to set the
	// `this.el` property.
	_setElement(el) {
		this.$el = el instanceof HTMLElement ? el : document.querySelector(el);
		this.el = this.$el;
	},

	// Set attributes from a hash on this view's element.  Exposed for
	// subclasses using an alternative DOM manipulation API.
	_setAttributes(attributes) {
		Object.keys(attributes).forEach((key) => {
			this.el.setAttribute(key, attributes[key]);
		});
	},

	eventFunc(selector, listener, event, ...args) {
		if (!selector) {
			listener.apply(this, [event, ...args]);
			return;
		}

		const { target } = event;
		let pathItem = target;

		do {
			if (pathItem.matches ? pathItem.matches(selector) : pathItem.msMatchesSelector(selector)) {
				event.delegateTarget = pathItem;
				listener.apply(this, [event, ...args]);
			}

			pathItem = pathItem.parentElement;
		} while (pathItem !== this.$el && pathItem !== document.documentElement && pathItem !== null);
	},

	// Add a single event listener to the view's element (or a child element
	// using `selector`). This only works for delegate-able events: not `focus`,
	// `blur`, and not `change`, `submit`, and `reset` in Internet Explorer.
	delegate(eventName, selectorParam, listenerParam) {
		let listener = listenerParam;
		let selector = selectorParam;

		if (typeof selector === 'function') {
			listener = selector;
			selector = null;
		}

		listener = this.eventFunc.bind(this, selector, listener);

		ViewEvent.addEventListener(this.el, `${eventName}.delegateEvents${this.cid}`, selector, listener);
		return this;
	},

	// Clears all callbacks previously bound to the view by `delegateEvents`.
	// You usually don't need to use this, but may wish to if you have multiple
	// Backbone views attached to the same DOM element.
	undelegateEvents() {
		if (this.$el) ViewEvent.removeEventListener(this.el, `.delegateEvents${this.cid}`);
		return this;
	},

	// A finer-grained `undelegateEvents` for removing a single delegated event.
	// `selector` and `listener` are both optional.
	undelegate(eventName, selectorParam, listenerParam) {
		let listener = listenerParam;
		let selector = selectorParam;

		if (typeof selector === 'function') {
			listener = selector;
			selector = null;
		}

		ViewEvent.removeEventListener(this.el, `${eventName}.delegateEvents${this.cid}`, selector, listener);
		return this;
	},
});

export default View;
